import type { ContentPage } from "../types";

// Numbers come from the app itself, so they stay true: minimum, advice and
// maximum from lib/train/clips.ts, times from lib/train/eta.ts (median and
// 80th percentile of 2,103 voices, Sep-Oct 2026). Re-check both when either
// file changes. Troubleshooting follows what /train and lib/train/retry.ts
// actually do (silence cut, repeats skipped, the two clean-up switches, the
// failure messages and "Try again"). Other tools' facts are from their own
// pages on 2026-10-17, listed in `sources`.
const page: ContentPage = {
  section: "blog",
  slug: "how-to-train-an-ai-voice-model",
  coverLabel: "Train your AI voice",
  title: "How to Train Your Own AI Voice Model (2026 Guide)",
  metaDescription:
    "Train an AI voice of yourself: how many minutes of audio you need, how to record it, how long training takes, why voices sound bad and free RVC options.",
  h1: "How to train your own AI voice model",
  kind: "Guide",
  stats: [
    { value: "3 min", label: "of voice is the minimum; 6+ sounds better" },
    { value: "~25 min", label: "to train on 10 minutes of audio" },
    { value: "0", label: "installs or graphics cards needed" },
  ],
  answer:
    "To train an AI voice model, record or upload at least 3 minutes of clean singing or speech in one voice (6 to 10 minutes sounds better), let Jammable cut the silence, name the voice and start training. A 10-minute dataset is usually ready in about 25 minutes, and you can then use the voice for covers and text-to-speech.",
  takeaways: [
    "You need at least 3 minutes of one clean voice; 6 to 10 minutes usually sounds better, and Jammable uses up to 60.",
    "A voice trained on 10 minutes of audio is usually ready in about 25 minutes; a full hour takes about 1 hour 45 minutes.",
    "Clean, dry, varied recordings matter more than length: one voice, a quiet room, no music and no effects.",
    "Training on Jammable comes with the Creator plan or a training pass. Local RVC tools such as Applio are free but need a capable computer.",
  ],
  queries: [
    "voice training",
    "train ai voice model",
    "how to make an ai voice of yourself",
    "how to make your own ai voice",
    "how to train ai voice",
    "rvc training",
    "train ai voice free",
    "how many minutes of audio to train an ai voice",
  ],
  published: "2026-10-17",
  updated: "2026-10-17",
  blocks: [
    {
      type: "md",
      text: `## What is AI voice training?

AI voice training teaches a voice model what one voice sounds like, from recordings of that voice. Once trained, the model can re-sing a vocal or read out text in that voice. (This guide is about AI voices, not singing lessons.)

Jammable trains **RVC** (Retrieval-based Voice Conversion) models, the kind most AI covers are made with. If you want to know how they work under the hood, read [what an RVC model is](/blog/what-is-rvc). For the wider picture of copying a voice, see [AI voice cloning](/blog/ai-voice-cloning).

## What you need

- **3 to 60 minutes of one voice.** 3 minutes is the minimum Jammable accepts; voices usually sound better with 6 minutes or more. Only singing or speech counts: silence is cut automatically and a file added twice only counts once.
- **Clean recordings.** One voice at a time, no backing vocals, no crowd, no music behind it, in a quiet room without echo.
- **Range.** Different notes, words and energy levels. A voice trained only on quiet speech will struggle to sing loudly.
- **MP3, WAV or M4A files**, or just your microphone: you can record straight into the page.

Use recordings of your own voice, or of someone who has agreed to it.`,
    },
    {
      type: "steps",
      heading: "How to make an AI voice of yourself, step by step",
      items: [
        {
          title: "Open Train and add your voice",
          body: "Go to [Train](/train) and upload files, or press Record and sing or talk. Record as many takes as you like.",
        },
        {
          title: "Check what gets kept",
          body: "Each file shows how much voice is left after the silence is removed. Trim anything that isn't the voice you want, like a cough, a second person or a noisy intro.",
        },
        {
          title: "Name it and choose who can use it",
          body: "Add a name and a picture, then pick *Private, only me* or *Public in the library*.",
        },
        {
          title: "Clean up the samples if needed",
          body: "Turn on *Remove instrumentals* if there is music under the vocals, and *Remove reverb* if the room echoes. Skip both for dry, clean recordings.",
        },
        {
          title: "Start training",
          body: "It runs on Jammable's servers, so you can close the page. You get an email when the voice is ready.",
        },
        {
          title: "Use it",
          body: "Your voice appears in your [Library](/library). Pick it on the [cover maker](/create) or for text-to-speech like any other voice.",
        },
      ],
    },
    {
      type: "md",
      text: `## How much audio do you need?

On Jammable, **at least 3 minutes and at most 60 minutes** of singing or speech, counted after the silence is cut. Voices usually sound better with **6 to 10 minutes** of clean, varied audio. The page counts as you add files, skips a file you already added, and tells you when you have enough.

More is not automatically better. 8 clean minutes train a better voice than 40 minutes recorded in a noisy room, and more audio means a longer wait. Other tools ask for very different amounts, from 10 to 30 seconds for an instant clone to 10 minutes or more for a full model (see the comparison below), because they make different kinds of model.`,
    },
    {
      type: "bars",
      heading: "How long does AI voice training take?",
      caption:
        "Typical (median) time from starting training to the voice being ready, by how much audio it learns from after silence is cut. 2,103 voices trained on Jammable, Sep to Oct 2026.",
      items: [
        {
          label: "Up to 10 minutes of audio",
          value: 25,
          display: "about 25 min",
        },
        { label: "10 to 20 minutes", value: 35, display: "about 35 min" },
        { label: "20 to 30 minutes", value: 55, display: "about 55 min" },
        { label: "30 to 45 minutes", value: 70, display: "about 1 h 10 min" },
        { label: "45 to 60 minutes", value: 105, display: "about 1 h 45 min" },
      ],
    },
    {
      type: "md",
      text: `Four in five voices are ready within about 30 minutes, 45 minutes, 1 hour 15 minutes, 1 hour 35 minutes and 2 hours 25 minutes for those same amounts of audio. Training runs on Jammable's side, so you can close the tab and wait for the email.

## Record it well

The recordings are the voice: nothing you change later fixes a voice that learned from noise.

- **Room.** Pick the quietest, softest room you have: a bedroom with curtains, a rug and a bed absorbs echo far better than a kitchen or a bathroom. Turn off fans, air conditioning and notifications.
- **Microphone.** A USB or headset mic is better than a laptop's built-in one, but a recent phone in a quiet room works. Use the same mic for every take.
- **Distance.** Stay about a hand's width to a forearm's length from the mic (roughly 15 to 30 cm, 6 to 12 inches), and keep that distance for every take so the tone stays consistent. Closer gets boomy and pops on "p" and "b"; further picks up the room.
- **Level.** Loud enough to be clear, never so loud that it distorts at your loudest note. Do a test take and listen back on headphones.
- **No effects.** Autotune, heavy compression and reverb get learned as part of the voice. Record dry and leave effects for the cover.

**What to sing or say.** Record the way you will use the voice. For singing, sing a cappella across your low, middle and high notes, soft and loud: scales, a few melodies you know well, your own songs. For speech, read varied sentences out loud, with questions, exclamations and calm passages, at your normal speed. Several short takes are fine; the voice learns from all of them.`,
    },
    {
      type: "callout",
      tone: "tip",
      title: "Quick recording checklist",
      text: "Quiet room with soft furnishings · one voice only, no music behind it · same mic and same distance every take · no autotune or reverb · low, middle and high notes · 6 to 10 minutes of voice after silence is cut · listen back on headphones before you upload.",
    },
    {
      type: "md",
      text: `## Speaking voice vs singing voice

A voice model learns the sounds it hears. If you train it on speech, it knows your tone well but has never heard you hold a note, so loud or high singing can come out strained. If you train it on singing, it handles covers best and still reads text in your voice.

- **For covers:** train mostly on singing, across your whole range.
- **For text-to-speech or voiceovers:** train on varied reading.
- **For both:** mix the two. Jammable takes up to 60 minutes, so there is room for some of each.

When you make a cover with a voice that sits higher or lower than the original singer, use the **Pitch** control on the cover maker. It is in semitones: going from a male to a female voice, try +12, and the other way, -12. For more on singing voices, see the [AI singing voice generator](/blog/ai-singing-voice-generator) guide.

## Why does my AI voice sound bad?

Most bad voices come from the training audio, so the fix is nearly always better samples.

- **Echoey or distant:** the room echo was learned. Record somewhere softer, or turn on *Remove reverb* and train again.
- **Music, hiss or another voice in the background:** the voice learned those too. Turn on *Remove instrumentals*, trim the parts with other people in the trim editor, and leave out noisy files.
- **Thin, robotic or buzzy:** usually too little or too similar audio. Add more varied takes and train again. Retrain rather than patch: cover settings can't add what the voice never learned.
- **Cracks or strains on high or loud notes:** the training audio didn't include them. Add takes in that range, or shift the cover's pitch so the song sits where your voice was trained.
- **Garbled harmonies in a cover:** turn on *Split out backing vocals* when you make it, so the voice only gets the lead vocal.
- **Sounds autotuned all the time:** autotune was on in the recordings. Record without it and add Autotune on the cover instead, where you can choose light, medium or strong.

**If training fails**, the voice's card in your Library says why. "We couldn't hear singing or speech to learn from in these samples" means the files need clean vocals with no music and no long silences. "One of your files couldn't be read" means saving it again as MP3 or WAV. If the problem was on Jammable's side, such as a dropped download, the card offers **Try again** on the same samples, up to two more times, and a training pass spent on a voice that fails is given back.`,
    },
    {
      type: "matrix",
      heading: "Train on Jammable vs train locally (RVC/Applio) vs other sites",
      matrix: {
        caption:
          "From each tool's own pages on 2026-10-17. Not stated: the pages we checked don't say.",
        tools: [
          "Jammable",
          "RVC / Applio (local)",
          "Kits.ai",
          "Lalals",
          "Covers.ai",
        ],
        rows: [
          {
            feature: "Audio needed",
            values: [
              "3 to 60 min (6+ sounds better)",
              "10 min or less, says RVC",
              "15–30 s instant; 10+ min pro",
              "10–30 s",
              "2–5 min",
            ],
          },
          {
            feature: "Ready in",
            values: [
              "About 25 min for 10 min of audio",
              "Depends on your graphics card",
              "Not stated",
              "90 min (Premium) or 24 h (Standard)",
              "About an hour",
            ],
          },
          {
            feature: "Cost to train",
            values: [
              "Creator plan ($9.99 first month, then $24.99/mo) or a training pass",
              "Free",
              "From $10/mo (Starter, 2 voice slots)",
              "$3 or $10 per training credit",
              "Voice pass or subscription",
            ],
          },
          {
            feature: "Needs your own graphics card",
            values: ["no", "yes", "no", "no", "no"],
          },
          {
            feature: "Keep the voice private",
            values: ["yes", "yes", "Not stated", "Not stated", "yes"],
          },
          {
            feature: "Live voice changing",
            values: ["no", "yes", "Not stated", "Not stated", "Not stated"],
          },
          {
            feature: "Runs in",
            values: [
              "Browser, any device",
              "Your computer (Windows, Linux; Applio also macOS)",
              "Browser or desktop app",
              "Browser",
              "Browser",
            ],
          },
        ],
      },
    },
    {
      type: "md",
      text: `**Jammable** is the simplest route: nothing to install, it cuts the silence and picks the training settings for you, and the finished voice sits next to 60,000+ others for covers and text-to-speech. People who already run RVC on their own computer can also [upload an existing RVC model](/library) (a .pth file plus its .index file) instead of training a new one.

**RVC or Applio on your own computer** gives you full control and costs nothing, and it does one thing Jammable doesn't: change your voice live, for calls and streams. In return you install it, find your own way around the training options, and need a capable machine. The original RVC project supports NVIDIA cards through CUDA and AMD and Intel through a CPU or DirectML setup, on Windows and Ubuntu. Applio, a popular RVC app, runs on Windows, macOS and Linux and says it will no longer get frequent updates.

**Kits.ai and Lalals** also offer quick clones from under a minute of audio, which is fast but, in Kits.ai's own words, less similar to the target than its professional clone. If you are choosing between Jammable and Kits.ai, see [Jammable vs Kits.ai](/compare/jammable-vs-kits-ai); for covers in general, the [best AI cover generators](/compare/best-ai-cover-generators).

## Can you train an AI voice for free?

Yes, but not on Jammable, and not without a capable computer.

- **On Jammable**, training needs the **Creator** plan ($9.99 the first month, then $24.99 a month), which includes unlimited voice training, or a training pass (each pass trains one voice). A free account without a pass can't start training. See [pricing](/pricing).
- **RVC and Applio** are free and open source (MIT licence). The cost is your hardware and setup time. Applio's README also links Google Colab notebooks, which run in Google's cloud instead of on your machine.
- **Free plans elsewhere** mostly leave training out: Kits.ai's free plan has no voice cloning, and Audimee's free plan has no custom voice slots.

## Training on your phone or computer

Training happens in the browser on any device, so there is nothing to install and no graphics card needed. On a phone, press Record on the [Train](/train) page and sing or talk straight into it, or upload MP3, WAV or M4A files. To hear what trained voices can do first, browse the [voice library](/models) or read about [AI voice models](/blog/ai-voice-models).`,
    },
    {
      type: "cta",
      href: "/train",
      label: "Train your voice",
      text: "Add a few minutes of your voice and Jammable does the rest.",
    },
  ],
  faqs: [
    {
      q: "How much audio do I need to train an AI voice?",
      a: "At least 3 minutes of singing or speech after silence is removed, and up to 60 minutes. Voices usually sound better with 6 to 10 minutes of clean, varied audio.",
    },
    {
      q: "How long does it take to train an AI voice model?",
      a: "It depends on the amount of audio. On Jammable, a voice trained on up to 10 minutes is usually ready in about 25 minutes, and one trained on a full hour in about 1 hour 45 minutes. You can close the page while it trains.",
    },
    {
      q: "How do I make an AI voice of myself?",
      a: "Record at least 3 minutes of yourself singing or talking in a quiet room (6 to 10 minutes is better), upload the files or record them on Jammable's Train page, name the voice and start training. You get an email when it's ready, and the voice appears in your Library for covers and text-to-speech.",
    },
    {
      q: "Can I train an AI voice for free?",
      a: "Not on Jammable: training needs the Creator plan ($9.99 the first month, then $24.99 a month) or a training pass. Open-source RVC tools such as Applio are free, but you run them on your own computer, which needs a capable graphics card, or in a cloud notebook.",
    },
    {
      q: "What is RVC training?",
      a: "RVC (Retrieval-based Voice Conversion) training builds a voice model from recordings of one voice. Jammable trains RVC models on its own servers, the same kind of model the wider AI cover community uses, and you can also upload an RVC model (.pth plus .index) you trained elsewhere.",
    },
    {
      q: "Can I train a voice from speech and use it to sing?",
      a: "Yes, but it sings best when it has heard singing. A voice trained only on quiet speech will struggle with loud or high notes, so add sung takes across your range if you want it for covers.",
    },
    {
      q: "Why does my AI voice sound robotic or echoey?",
      a: "Almost always the training audio. Echo comes from the room, buzz and robotic tones from too little or too similar audio, and background sounds from music or other voices in the files. Record dry and clean, use Remove reverb or Remove instrumentals if needed, and train again.",
    },
    {
      q: "Can I train an AI voice on my phone?",
      a: "Yes. Jammable's Train page works in a phone browser: press Record and sing or talk, or upload MP3, WAV or M4A files. Training runs on Jammable's servers, so the phone does none of the work.",
    },
    {
      q: "Can I keep my AI voice private?",
      a: "Yes. Choose Private when you set it up and only you can see and use the voice. You can also share it publicly in the voice library.",
    },
    {
      q: "What file types can I upload?",
      a: "MP3, WAV and M4A. You can also record directly from your microphone on the training page.",
    },
  ],
  related: [
    { label: "Train your own voice", href: "/train" },
    { label: "What is an RVC model?", href: "/blog/what-is-rvc" },
    { label: "AI voice cloning", href: "/blog/ai-voice-cloning" },
    { label: "Browse 60,000+ AI voices", href: "/models" },
    {
      label: "Best AI cover generators",
      href: "/compare/best-ai-cover-generators",
    },
    { label: "Pricing", href: "/pricing" },
  ],
  sources: [
    {
      label: "RVC WebUI on GitHub",
      href: "https://github.com/RVC-Project/Retrieval-based-Voice-Conversion-WebUI",
    },
    { label: "Applio on GitHub", href: "https://github.com/IAHispano/Applio" },
    { label: "Kits.ai pricing", href: "https://www.kits.ai/pricing" },
    {
      label: "Kits.ai instant voice cloning",
      href: "https://www.kits.ai/tools/instant-voice-cloning",
    },
    {
      label: "Kits.ai: instant vs professional cloning",
      href: "https://help.kits.ai/hc/en-us/articles/40742972060435-How-is-Instant-Voice-Cloning-different-from-Professional-Voice-Cloning",
    },
    { label: "Lalals voice cloning", href: "https://lalals.com/voice-cloning" },
    {
      label: "Lalals: voice training tiers",
      href: "https://lalals.com/blog/how-to-make-ai-song-cover-lalals",
    },
    {
      label: "Covers.ai custom voice",
      href: "https://covers.ai/custom-ai-voice",
    },
    { label: "Audimee pricing", href: "https://www.audimee.com/pricing" },
  ],
};

export default page;
